import React, { useState } from 'react';
import NavBar from '../components/Navbar/NavBar';
import Footer from '../components/Footer';
import { useNavigate } from 'react-router-dom';
import CheckIcon from '../images/checkIcon.svg';

const internships = [
  {
    key: 'paid',
    title: 'Project Internship',
    shortDesc: 'Work on live client projects, earn a stipend, and gain real-world experience.',
    whoCanApply: 'Students or recent graduates with relevant project experience in technology.',
    whatYouGet: [
      'Offer Letter',
      'Completion Letter',
      'Recommendation Letter',
      'Work on live client projects',
      'Performance-based stipend increase',
      'Curated YouTube playlist to grasp project concepts',
    ],
    stipend: '₹2,000/month (may increase with performance)',
    joiningFee: 'None (revnue generating entity for company)',
    duration: 'Minimum 2 months, extendable based on project requirements (fully remote)',
    tnc: [
      'Subject to availability (currently no openings).',
      'Selection via a single, project-based interview (no DSA required).',
      'Candidates must showcase their projects in relevant technologies.',
      'Remote only.',
    ],
    applyPrefill: 'I am interested in the Paid Internship program. Please provide more details.',
    isOpen: true,
  },
  {
    "key": "industry",
    "title": "Industry-Ready Internship Program",
    "shortDesc": "Step into real-world projects, sharpen your skills, and get expert-reviewed experience to boost your career.",
    "whoCanApply": "Students, graduates, or professionals looking for hands-on industry experience, or those who couldn’t clear the paid internship interview.",
    "whatYouGet": [
      "Official Offer Letter",
      "Internship Completion Certificate",
      "Industry projects designed to strengthen your expertise in in-demand technologies.",
      "Code reviews and feedback from industry experts",
      "Curated learning resources to master project concepts",
      "Access to a professional work-like environment"
    ],
    "stipend": "No stipend (program is non-revenue generating for company).",
    "joiningFee": "₹499 (one-time, non-refundable)",
    "duration": "1 month (fully remote)",
    "tnc": [
      "Learning is 100% project-driven — no boring tutorials or assignments.",
      "Fully remote; you’ll need your own laptop and internet connection.",
      "Internship fees are strictly non-refundable.",
      "Leave is limited and must be pre-approved; unnotified absence may lead to termination.",
      "Maintain confidentiality of all work — sharing code/data is strictly prohibited.",
      "All project work and code developed remain the property of Technodevotion LLP.",
      "This internship is purely training-focused and not a job offer.",
      "Dedicated 1:1 mentorship is not included — structured resources and a query portal are provided instead."
    ],
    "applyPrefill": "I’m interested in the Industry-Ready Internship Program. Please share more details.",
    "isOpen": true
  },  
];

const faqs = [
  {
    question: 'Are all internships remote?',
    answer: 'Yes, all internships at Technodevotion are remote. We believe in a flexible, work-from-anywhere policy.'
  },
  {
    question: 'What is the selection process for the Paid Internship?',
    answer: 'Selection is through a single, project-based interview. You will be asked to showcase your projects and relevant technical knowledge. No DSA is required.'
  },
  {
    question: 'Can I apply for the Industry-Ready Internship if I do not clear the Paid Internship interview?',
    answer: 'Yes, the Industry-Ready Internship Program is open to anyone, including those who do not clear the Paid Internship interview.'
  },
  {
    question: 'Will I get to interact with industry experts?',
    answer: 'Yes, in the Industry-Ready Internship Program, you will receive code reviews from industry experts and a 1-on-1 consultation at the end of the program.'
  },
  {
    question: 'Will I know who the industry experts are?',
    answer: 'No, the identity of industry experts reviewing your code will not be revealed, as they are not full-time reviewers or employees.'
  },
  {
    question: 'Is there a joining fee for the internships?',
    answer: 'There is no joining fee for the Paid Internship. The Industry-Ready Internship Program has a one-time joining fee of ₹999.'
  },
  {
    question: 'What documents will I receive after completion?',
    answer: 'You will receive an Offer Letter and Completion Letter for both internships. Paid interns also receive a Recommendation Letter.'
  },
];

const Accordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);
  return (
    <div className="w-full max-w-6xl mx-auto mt-16 bg-gray-700 rounded-2xl shadow-xl py-8 px-4 mx-4 xl:mx-0">
      <h2 className="text-3xl font-extrabold text-primary mb-8 border-b-2 border-primary pb-3 drop-shadow-lg text-center tracking-wide">Frequently Asked Questions</h2>
      {faqs.map((faq, idx) => (
        <div key={idx} className="mb-2">
          <button
            className="w-full text-left py-4 px-6 font-semibold focus:outline-none flex justify-between items-center rounded-t-lg transition bg-gray-700 hover:bg-primary/20 border-b border-primary text-white"
            onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
            style={{ borderBottom: openIndex === idx ? 'none' : '1px solid #f9690f' }}
          >
            <span className="text-white">{faq.question}</span>
            <span className="ml-2 text-2xl font-bold text-primary">{openIndex === idx ? '-' : '+'}</span>
          </button>
          {openIndex === idx && (
            <div className="px-6 pb-4 text-white bg-gray-700 border-b-2 border-primary rounded-b-lg animate-fadeIn">
              {faq.answer}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const Internship = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState([]);
  const [modalTitle, setModalTitle] = useState('');
  const [seeMore, setSeeMore] = useState({});
  const navigate = useNavigate();

  const handleTnC = (title, tnc) => {
    setModalTitle(title + ' - Terms & Conditions');
    setModalContent(tnc);
    setModalOpen(true);
  };

  const handleApply = (program) => {
    navigate('/contact', { state: { isDemoRequest: false, productName: '', prefillMessage: program.applyPrefill } });
  };

  const toggleSeeMore = (key) => {
    setSeeMore((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      <NavBar />
      <div className="flex flex-col items-center min-h-screen py-12 lg:py-24 bg-gradient-to-r from-primary to-[#FF8C42]">
        <div className="w-full max-w-6xl bg-white rounded-2xl shadow-2xl p-8 flex flex-col lg:flex-row gap-16 justify-center items-stretch mx-4 xl:mx-0">
          {internships.map((program, idx) => (
            <section
              key={program.key}
              className={`flex-1 px-4 ${idx === 0 ? 'lg:border-r-2 border-primary' : ''}`}
            >
              <h2 className="text-2xl font-bold text-primary mb-3 tracking-wide uppercase">{program.title}</h2>
              <p className="text-gray-800 mb-6 text-lg italic">{program.shortDesc}</p>
              <div className="mb-4">
                <h3 className="font-semibold mb-1 text-primary">Who can apply?</h3>
                <p className="text-gray-900 mb-3 text-base">{program.whoCanApply}</p>
              </div>
              <div className="mb-4">
                <h3 className="font-semibold mb-1 text-primary">What you will get:</h3>
                <ul className="pl-4 space-y-1">
                  {(seeMore[program.key] ? program.whatYouGet : program.whatYouGet.slice(0, 2)).map((item, idx2) => (
                    <li key={idx2} className="mb-1 text-gray-800 flex items-center gap-2">
                      <img src={CheckIcon} alt="check" className="inline w-6 h-6" /> {item}
                    </li>
                  ))}
                </ul>
                {program.whatYouGet.length > 2 && (
                  <button
                    className="text-primary underline mt-2 hover:text-orange-600 transition font-medium"
                    onClick={() => toggleSeeMore(program.key)}
                    type="button"
                  >
                    {seeMore[program.key] ? 'See less' : 'See more'}
                  </button>
                )}
              </div>
              <div className="mb-2 text-base">
                <span className="font-semibold text-primary">Stipend:</span> <span className="text-gray-900">{program.stipend}</span>
              </div>
              <div className="mb-2 text-base">
                <span className="font-semibold text-primary">Operations Cost:</span> <span className="text-gray-900">{program.joiningFee}</span>
              </div>
              <div className="mb-2 text-base">
                <span className="font-semibold text-primary">Duration:</span> <span className="text-gray-900">{program.duration}</span>
              </div>
              <button
                className="text-primary underline mt-2 mb-4 hover:text-orange-600 transition font-medium"
                onClick={() => handleTnC(program.title, program.tnc)}
                type="button"
              >
                Terms & Conditions
              </button>
              <br />
              <button
                className={`mt-4 px-8 py-3 rounded-lg font-bold text-lg shadow transition-all duration-200 ${program.isOpen ? 'bg-primary text-white hover:bg-orange-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
                onClick={() => program.isOpen && handleApply(program)}
                disabled={!program.isOpen}
              >
                {program.isOpen ? 'Apply Now' : 'Currently Closed'}
              </button>
            </section>
          ))}
        </div>
        {/* Modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
            <div className="bg-white rounded-xl shadow-2xl p-8 max-w-lg w-full relative animate-fadeIn">
              <button
                className="absolute top-2 right-2 text-gray-500 hover:text-orange-600 text-2xl font-bold"
                onClick={() => setModalOpen(false)}
                aria-label="Close"
              >
                ×
              </button>
              <h3 className="text-2xl font-bold mb-4 text-primary">{modalTitle}</h3>
              <ul className="list-disc pl-6 text-gray-800 space-y-2">
                {modalContent.map((item, idx) => (
                  <li key={idx} className="mb-2">{item}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
        {/* FAQ Accordion */}
        <Accordion faqs={faqs} />
      </div>
      <Footer />
    </>
  );
};

export default Internship; 